/**
 * @file QSightMetricCardGroup.tsx
 * @description This file contains the QSightMetricCardGroup component, which is used to display a group of metric cards in a grid layout.
 * It allows for both horizontal and vertical orientations.
 * It can display basic metric cards or chart metric cards based on the provided props.
 * @param, metricType, groupTitle, metrics, orientation, error
 **/

import React from 'react';
import QSightBasicMetricCard, { QSightBasicMetricCardProps } from './QSightBasicMetricCard/QSightBasicMetricCard';
import QSightChartMetricCard, { QSightChartMetricCardProps } from './QSightChartMetricCard/QSightChartMetricCard';
import { MetricCardType, Orientation } from './MetricCardEnumTypes';
import { Card, CardHeader, Spinner } from 'reactstrap';
import './QSightMetricCardGroup.style.css';

export interface QSightMetricCardGroupProps {
    metricType?: MetricCardType;
    groupTitle?: string;
    metrics: (QSightBasicMetricCardProps | QSightChartMetricCardProps)[];
    orientation?: Orientation;
    error?: string;
    loading?: boolean;
}

// Type guards
const isBasicMetric = (metric: QSightBasicMetricCardProps | QSightChartMetricCardProps): metric is QSightBasicMetricCardProps => {
    return (metric as QSightBasicMetricCardProps).DataValues !== undefined;
};

const QSightMetricCardGroup: React.FC<QSightMetricCardGroupProps> = ({
    metricType = MetricCardType.Basic,
    groupTitle,
    metrics = [],
    orientation = Orientation.Horizontal,
    error,
    loading = false,
}) => {
    const hasMetrics = Array.isArray(metrics) && metrics.length > 0;

    const renderHeader = () => {
        if (!groupTitle) return null;
        return (
            <Card className="my-2">
                <CardHeader className="metric-group-title bg-grey text-black">
                    {groupTitle.trim() || 'No Title Available'}
                </CardHeader>
            </Card>
        );
    };

    const renderStatus = () => {
        if (loading) {
            return (
                <div className="p-3 text-center">
                    <Spinner color="primary" />
                </div>
            );
        }

        if (error) {
            return <h5 className="text-error p-3">Error: {error}</h5>;
        }

        if (!hasMetrics) {
            return <h5 className="text-warning p-3">No metrics available to display.</h5>;
        }

        return null;
    };

    const renderMetrics = () => {
        if (loading || error || !hasMetrics) return null;

        return (
            <div className={`metric-card-group ${orientation}`}>
                {metrics.map((metric, index) => (
                    <section key={(metric as any).metricId ?? index} className="metric-card-wrapper">
                        {metricType === MetricCardType.Basic && isBasicMetric(metric) ? (
                            <QSightBasicMetricCard
                                {...metric}
                                DataValues={metric.DataValues
                                    ?? [{ value: 0 }, { value: 0 }]}
                            />
                        ) : (
                            <QSightChartMetricCard
                                {...(metric as QSightChartMetricCardProps)}
                                value={(metric as QSightChartMetricCardProps).value
                                    ?? (metric as any)?.DataValues?.[0]?.value}
                            />
                        )}
                    </section>
                ))}
            </div>
        );
    };

    return (
        <div className="mx-3">
            {renderHeader()}
            {renderStatus()}
            {renderMetrics()}
        </div>
    );
};

export default QSightMetricCardGroup;

